/**
 * Opening files from the Signing page the way the Documents page does.
 *
 * Two kinds of file live on this page, and they open differently — exactly as
 * they do on /documents:
 *  - a signed CONTAINER (.asice/.bdoc/.ddoc) is a sealed file the browser
 *    cannot render, so "open" is the shared signature panel, which lists what
 *    the container encloses and opens each of those in the document viewer;
 *  - anything else — a signed PDF, a source document in a container or a
 *    session — opens in the shared `DocumentViewerDialog`.
 *
 * Rows, the detail panel's file lists and the right-click menu all go through
 * `useSigningPreview()` (`signingPreviewContext.ts`). The viewer itself is the
 * one the enclosing `SignaturePanelProvider` already owns for container
 * contents — one preview state, one held blob and one error toast per page —
 * so this must sit INSIDE that provider, mounted with the page's `buildingId`.
 */

import { useMemo, type ReactNode } from "react";

import { isSignedContainerFile } from "@/domains/documents/constants/fileTypes";
import {
  useSignaturePanel,
  useSignaturePanelViewer,
} from "@/domains/documents/signing/signaturePanelContext";
import { canQuickViewDocumentRow } from "@/domains/documents/hooks/useDrawingViewer";
import { usePermissions } from "@/hooks/usePermissions";
import { SigningPreviewContext, type SigningPreviewApi } from "./signingPreviewContext";
import type { SigningFile, SigningListItem } from "./types";

const isContainerItem = (item: SigningListItem) =>
  item.kind === "container" && item.documentId != null && isSignedContainerFile(item.title);

export function SigningPreviewProvider({
  buildingId,
  children,
}: {
  buildingId: number;
  children: ReactNode;
}) {
  const panel = useSignaturePanel();
  // The panel provider's viewer, which also toasts a failed open (translated
  // text only). The one failure with a specific remedy — a drawing the viewer
  // may not open — is never offered at all (canOpenFile).
  const viewer = useSignaturePanelViewer();

  // A DWG opens in the Drawings viewer, which needs its own grant — the same
  // gate the Documents list's quick view uses.
  const { canViewDrawingCompareTool } = usePermissions();

  // The viewer opens only once the file has downloaded, which can take seconds
  // for a large signed PDF; the id lets the control that asked show a spinner
  // instead of reading as dead. It is the viewer's own record of what is
  // loading, so a click on another file — here or in the panel — moves it, and
  // nothing stale is left behind once the load settles.
  const openPreviewByDoc = viewer?.openPreviewByDoc;
  const loadingId = viewer?.loadingDocumentId ?? null;
  const openingId = loadingId != null ? Number(loadingId) : null;
  const api = useMemo<SigningPreviewApi>(() => {
    const canOpenFile = (file: SigningFile) =>
      !!openPreviewByDoc &&
      !!file.filePath &&
      canQuickViewDocumentRow(
        {
          filename: file.filename,
          content_type: file.contentType ?? null,
          state: file.state ?? null,
        },
        canViewDrawingCompareTool
      );

    const openFile = (file: SigningFile) => {
      // A second click on the file already downloading would restart it.
      if (!openPreviewByDoc || !file.filePath || openingId === file.id) return;
      void openPreviewByDoc({
        id: file.id,
        filename: file.filename,
        file_path: file.filePath,
        building_id: buildingId,
        content_type: file.contentType ?? null,
        size: file.size,
        created_at: file.createdAt ?? "",
        created_by_name: file.uploadedBy ?? undefined,
      });
    };

    const asFile = (item: SigningListItem): SigningFile | null =>
      item.kind === "container" && item.documentId != null
        ? {
            id: item.documentId,
            filename: item.title,
            size: item.size ?? 0,
            filePath: item.filePath,
            uploadedBy: item.senderName || null,
            contentType: item.contentType ?? null,
            state: item.documentState ?? null,
            createdAt: item.createdAt,
          }
        : null;

    return {
      openingId,
      isContainerItem,
      canOpenItem: (item) => {
        if (isContainerItem(item)) return panel != null;
        const file = asFile(item);
        return file != null && canOpenFile(file);
      },
      openItem: (item) => {
        if (isContainerItem(item)) {
          panel?.open({ containerId: String(item.documentId) });
          return;
        }
        const file = asFile(item);
        if (file) openFile(file);
      },
      canOpenFile,
      openFile,
    };
  }, [buildingId, panel, openPreviewByDoc, openingId, canViewDrawingCompareTool]);

  return <SigningPreviewContext.Provider value={api}>{children}</SigningPreviewContext.Provider>;
}
